import { useEffect } from "react";
import { useTour } from "@reactour/tour";

function waitForSelector(selector, timeout = 5000) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(selector)) {
      resolve();
      return;
    }

    const observer = new MutationObserver(() => {
      if (document.querySelector(selector)) {
        observer.disconnect();
        resolve();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    setTimeout(() => {
      observer.disconnect();
      reject(new Error("Timeout waiting for selector: " + selector));
    }, timeout);
  });
}

export default function TourAutoAdvanceListener() {
  const { isOpen, currentStep, steps, setCurrentStep } = useTour();

  useEffect(() => {
    if (!isOpen) return;

    const step = steps[currentStep];
    if (!step?.advanceOn) return;

    const handler = async (e) => {
      const clicked = e.target.closest(step.advanceOn);
      if (!clicked) return;

      const nextStep = steps[currentStep + 1];
      if (!nextStep?.selector) {
        setCurrentStep((s) => s + 1);
        return;
      }

      try {
        await waitForSelector(nextStep.selector, nextStep.waitMs || step.waitMs || 5000);
      } catch (err) {
        // The next step's element never appeared (a slow load, a different screen): move on anyway - the tour shows
        // that step in the middle of the screen rather than freezing on a step whose button is hidden.
        console.warn(err.message);
      }
      setCurrentStep((s) => s + 1);
    };

    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, [isOpen, currentStep, steps, setCurrentStep]);

  return null;
}
